import { useState, useEffect, useLayoutEffect } from 'react';
import { MockHook, useQueryString } from '@jupiterone/jupiter-web-toolkit';
import { InAppNotification } from '@jupiterone/notification-types';
import { generateMockNotifications } from 'navbar/src/notifications/graphql/generateMockNotifications';

const NEW_NOTIFICATIONS_DURATION = 7000;
const POPOVER_ANIMATION_DELAY = 500;

const getNewNotifications = (
  notifications: InAppNotification[],
  cutoff: number
) => {
  const newNotifications = notifications?.filter(
    (n) => !n.readDate && n.createdAt >= cutoff
  );
  return newNotifications;
};

const getLeastRecentUnreadTimestamp = (
  unreadNotifications: InAppNotification[]
) => {
  const leastRecentUnreadNotification =
    unreadNotifications[unreadNotifications.length - 1];
  return leastRecentUnreadNotification
    ? leastRecentUnreadNotification.createdAt
    : Date.now();
};

export interface NewNotificationsProps {
  unreadNotifications: InAppNotification[];
  onClick: () => void;
  children: React.ReactNode;
}
const useNewNotificationsPopoverLogic = ({
  unreadNotifications,
  onClick,
  children,
}: NewNotificationsProps) => {
  const [
    leastRecentUnreadTimestamp,
    setLeastRecentUnreadTimestamp,
  ] = useState<number>(Date.now());

  // This is necessary to prevent the popover content
  // from changing as the close animation is executing
  // We wait the default delay duration of the component before
  // unrendering the content (POPOVER_ANIMATION_DELAY)
  const [
    isNewNotificationsPopoverOpen,
    setIsNewNotificationsPopoverOpen,
  ] = useState<boolean>(false);

  // if the settings page is open, don't show the popover
  const [isSettingsPageOpen] = useQueryString('settings', '');

  // When notifications update, check which, if any, notifications are new
  const newNotifications = getNewNotifications(
    unreadNotifications,
    leastRecentUnreadTimestamp
  );

  // Use the timestamp of the oldest, unread notification to determine
  // Which notifications should be shown
  useEffect(() => {
    setLeastRecentUnreadTimestamp(
      getLeastRecentUnreadTimestamp(unreadNotifications)
    );
  }, [JSON.stringify(unreadNotifications)]);

  useEffect(() => {
    setIsNewNotificationsPopoverOpen(newNotifications.length > 0);
  }, [newNotifications.length]);

  useLayoutEffect(() => {
    // Close after 5 seconds
    setTimeout(() => {
      handleDismissNewNotifications();
    }, NEW_NOTIFICATIONS_DURATION);
  }, [newNotifications.length]);

  const handleDismissNewNotifications = () => {
    setIsNewNotificationsPopoverOpen(false);
    setTimeout(() => {
      setLeastRecentUnreadTimestamp(Date.now());
    }, POPOVER_ANIMATION_DELAY);
  };

  const handleNewNotificationsClick = () => {
    handleDismissNewNotifications();
    onClick();
  };

  return {
    children,
    newNotifications,
    isNewNotificationsPopoverOpen: isSettingsPageOpen
      ? false
      : isNewNotificationsPopoverOpen,
    handleNewNotificationsClick,
    handleDismissNewNotifications,
  };
};
export type NewNotificationsPopoverComponentProps = ReturnType<
  typeof useNewNotificationsPopoverLogic
>;

export const MOCK_NEW_NOTIFICATIONS_POPOVER_PROPS: NewNotificationsPopoverComponentProps = {
  children: '',
  isNewNotificationsPopoverOpen: true,
  newNotifications: generateMockNotifications(),
  handleNewNotificationsClick: MockHook.mockFunction,
  handleDismissNewNotifications: MockHook.mockFunction,
};

export default MockHook.useMockHook(
  useNewNotificationsPopoverLogic,
  MOCK_NEW_NOTIFICATIONS_POPOVER_PROPS
);
